import { EventType } from "@/analytics/analytics-events";
import { SheetModeControls } from "@/components/r3f/controls/sheet-mode-controls";
import { AnnotationsRenderer } from "@/components/r3f/renderers/annotations/annotations-renderer";
import { DesaturationSheetsPipeline } from "@/components/r3f/renderers/desaturation-pipeline";
import { MeasurementsRenderer } from "@/components/r3f/renderers/measurements/measurements-renderer";
import { PathWaypointTarget } from "@/components/r3f/renderers/odometry-paths/odometry-path-renderer";
import { OdometryPathsRenderer } from "@/components/r3f/renderers/odometry-paths/odometry-paths-renderer";
import { PinMarkerRenderer } from "@/components/r3f/renderers/pin-marker/pin-marker-renderer";
import { isAreaSection } from "@/components/ui/tree/capture-tree/capture-tree-discriminator";
import { PREVIEW_CLOSE_DELAY_MS } from "@/data-preparation-tool/ui/scan-preview";
import { IElementWaypoint } from "@/hooks/use-placeholders";
import { useViewOverlayRef } from "@/hooks/use-view-overlay-ref";
import { SheetObject, useValidateCacheSize } from "@/object-cache";
import { selectIsLocationMarkerCreationEnabled } from "@/store/location-marker-slice";
import { Measurement } from "@/store/measurement-tool-slice";
import { selectModeIsTransitioning } from "@/store/mode-selectors";
import { useAppSelector } from "@/store/store-hooks";
import { selectActiveTool } from "@/store/ui/ui-selectors";
import { ToolName } from "@/store/ui/ui-slice";
import { selectObjectVisibility } from "@/store/view-options/view-options-selectors";
import { ViewObjectTypes } from "@/store/view-options/view-options-slice";
import { PickingToolsCallbacks } from "@/tools/use-picking-tools-callbacks";
import { IElementPhotogrammetryScan } from "@/types/ielement-extra-types";
import { IS_DEV } from "@/utils/env-constants";
import {
  isIElementVideoModeCopy,
  MapWaypointsRendererRef,
  selectIElementWorldPosition,
  useTypedEvent,
  useWaypointContext,
  WaypointDraggedEvent,
} from "@faro-lotv/app-component-toolbox";
import { Analytics } from "@faro-lotv/foreign-observers";
import {
  IElement,
  IElementGenericAnnotation,
  IElementGenericImgSheet,
  IElementImg2d,
  IElementImg360,
  IElementRoomSection,
  IElementType,
  IElementTypeHint,
  type IElementGenericOdometryPath,
} from "@faro-lotv/ielement-types";
import { ThreeEvent, useThree } from "@react-three/fiber";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type JSX,
} from "react";
import { Box3, Plane, Vector3 } from "three";
import { calculateLocalPoseForMovedWaypoint } from "./edit-waypoints/calculate-local-pose-for-moved-waypoints";
import { createNewLocationMarker } from "./edit-waypoints/create-new-location-marker";
import {
  useEditWaypointsStore,
  useEditWaypointsTransformOverrides,
} from "./edit-waypoints/edit-waypoints-store";
import { LocationMarkerNameEditor } from "./edit-waypoints/location-marker-name-editor";
import { SceneContextMenuData } from "./scene-context-menu/use-scene-context-menu";
import { SheetModeRenderOrders } from "./sheet-mode-render-orders";
import { SheetWaypoints } from "./sheet-waypoints";
import { SheetsRenderer } from "./sheets-renderer";
import { useComputeAllowedWaypointInteractions } from "./use-compute-allowed-waypoint-interactions";
import { useOnWaypointsSelectedInTree } from "./use-on-waypoints-selected-in-tree";
import { useWaypointsSelectedTextures } from "./use-waypoints-selected-textures";
import { WaypointPreview } from "./waypoint-preview";

type SheetSceneBaseProps = {
  /** The current active sheets */
  sheets: Array<SheetObject | undefined>;

  /** Sheet whose elevation will be used for paths and 2D items */
  sheetElementForElevation?: IElementGenericImgSheet;

  /** The current reference element */
  referenceElement?: IElement;

  /** The list of paths to render */
  paths: IElementGenericOdometryPath[];

  /** The list of panoramas currently showed */
  panos: IElementImg360[];

  /** The list of photos currently showed */
  photos: IElementImg2d[];

  /** The list of orthomosaics currently showed */
  orthomosaics: IElementPhotogrammetryScan[];

  /** The list of the current location markers */
  locationMarkers: IElementRoomSection[];

  /** The list of annotations to render on the map */
  annotations?: IElementGenericAnnotation[];

  /** The list of measurements from the store, to render on the map */
  measurements?: Measurement[];

  /** Optional clipping planes to clip the scene with */
  clippingPlanes?: Plane[];

  /** Flag to make the sheet transparent */
  transparentSheet?: boolean;

  /** Callback issued when a context menu is requested for an element in the scene */
  onSceneContextMenu?(data: SceneContextMenuData): void;

  /** Callback when a placeholder is clicked */
  onPlaceholderClicked?(target: IElementWaypoint): void;

  /** Callback when a trajectory waypoint without a panorama is clicked */
  onTrajectoryPointClicked?(target: PathWaypointTarget): void;

  /** Callback on path activated */
  onPathActivated?(
    ev: ThreeEvent<MouseEvent>,
    path: IElementGenericOdometryPath,
    boundingBox: Box3,
  ): void;

  /** Callback when the sheet is clicked which gives the clicked position */
  onSheetClick?(pos: Vector3): void;

  /** Boolean to indicate if the sheet scene is used in minimap */
  isInMinimap?: boolean;

  /** Picking tool callback handlers to manage sheet interactions*/
  pickingToolsCallbacks?: PickingToolsCallbacks;

  /** Whether the waypoint moving tool should be active. @default false */
  isWaypointMoveActive: boolean;

  /** Whether the sheet scene should load views of the active sheets @default false */
  enableSheetViews?: boolean;
};

export type SheetSceneBaseApi = {
  /** Hides the waypoint preview, until the user hovers over it again. */
  hideWaypointPreview(): void;
};

/**
 * @returns the basic scene to render in sheet mode
 */
export const SheetSceneBase = forwardRef<
  SheetSceneBaseApi,
  SheetSceneBaseProps
>(function SheetSceneBase(
  {
    sheets,
    sheetElementForElevation,
    referenceElement,
    paths,
    panos,
    photos,
    orthomosaics,
    locationMarkers,
    annotations,
    measurements,
    clippingPlanes,
    onSceneContextMenu,
    onPlaceholderClicked,
    onTrajectoryPointClicked,
    onPathActivated,
    onSheetClick,
    isInMinimap = false,
    transparentSheet,
    pickingToolsCallbacks,
    isWaypointMoveActive,
    enableSheetViews = false,
  }: SheetSceneBaseProps,
  ref,
): JSX.Element {
  const camera = useThree((s) => s.camera);

  const isActiveElementVideoModeCopy =
    referenceElement && isIElementVideoModeCopy(referenceElement);

  const shouldShowPath = !isWaypointMoveActive && !isActiveElementVideoModeCopy;

  const isTransitioning = useAppSelector(selectModeIsTransitioning);

  const position = useAppSelector(
    selectIElementWorldPosition(sheetElementForElevation?.id),
  );

  const activeTool = useAppSelector(selectActiveTool);
  const isLocationMarkerCreationEnabled = useAppSelector(
    selectIsLocationMarkerCreationEnabled,
  );

  const {
    addNewLocationMarker,
    newLocationMarkers,
    setTransformOverride,
    updateNewLocationMarkers,
    editingLocationMarkerId,
    editingRightAfterCreation,
    setEditingLocationMarkerId,
  } = useEditWaypointsStore();

  const editingLocationMarker = useMemo(
    () =>
      newLocationMarkers.find(
        (marker) => marker.id === editingLocationMarkerId,
      ),
    [editingLocationMarkerId, newLocationMarkers],
  );

  const hasTrajectoryVisibilityOn = useAppSelector(
    selectObjectVisibility(ViewObjectTypes.trajectories),
  );
  const activePathIds = useMemo(() => {
    if (
      isAreaSection(referenceElement) ||
      (referenceElement?.type === IElementType.section &&
        referenceElement.typeHint === IElementTypeHint.dataSetCapture) ||
      // If the visibility of the trajectory is off, let's not consider this path
      // as active, so it's not rendered. If the option is on, then the path is
      // flagged as active, so that the proper rendering style can be used
      (isInMinimap && !hasTrajectoryVisibilityOn)
    ) {
      // No active paths when the reference is an element that can contain
      // multiple paths.
      return [];
    }

    return paths.map((p) => p.id);
  }, [referenceElement, paths, isInMinimap, hasTrajectoryVisibilityOn]);

  const [hoveredPlaceholder, setHoveredPlaceholder] =
    useState<IElementWaypoint>();
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const isPreviewHoveredRef = useRef(false);

  const clearHoverPreview = useCallback(() => {
    isPreviewHoveredRef.current = false;
    setHoveredPlaceholder(undefined);
  }, []);

  // when dragging any waypoint, reset newLocationMarker to close name editor
  const waypointContext = useWaypointContext();
  useTypedEvent<WaypointDraggedEvent>(waypointContext?.waypointDragged, () => {
    setEditingLocationMarkerId(undefined);
  });

  const clickOnSheet = useCallback(
    (e: ThreeEvent<MouseEvent>) => {
      onSheetClick?.(e.point);
      if (
        activeTool === ToolName.editWaypoints &&
        isLocationMarkerCreationEnabled
      ) {
        Analytics.track(EventType.placeLocationMarker);
        const locationMarker = createNewLocationMarker(e.point);
        addNewLocationMarker(locationMarker);
      }
    },
    [
      activeTool,
      addNewLocationMarker,
      isLocationMarkerCreationEnabled,
      onSheetClick,
    ],
  );

  const placeholders = useMemo(
    () =>
      activeTool === ToolName.editWaypoints
        ? [
            ...panos,
            ...photos,
            ...locationMarkers,
            ...orthomosaics,
            ...newLocationMarkers,
          ]
        : [...panos, ...photos, ...locationMarkers, ...orthomosaics],
    [
      activeTool,
      locationMarkers,
      newLocationMarkers,
      orthomosaics,
      panos,
      photos,
    ],
  );

  const onWaypointClicked = useCallback(
    (target: IElementWaypoint) => {
      const locationMarker = newLocationMarkers.find(
        (marker) => marker.id === target.id,
      );
      setEditingLocationMarkerId(locationMarker?.id);

      if (activeTool !== ToolName.editWaypoints && onPlaceholderClicked) {
        onPlaceholderClicked(target);
      }
    },
    [
      activeTool,
      newLocationMarkers,
      onPlaceholderClicked,
      setEditingLocationMarkerId,
    ],
  );

  const onPathWaypointClicked = useCallback(
    (target: PathWaypointTarget) => {
      if (target.element) {
        onPlaceholderClicked?.(target.element);
      } else {
        onTrajectoryPointClicked?.(target);
      }
    },
    [onPlaceholderClicked, onTrajectoryPointClicked],
  );

  const transformOverrides = useEditWaypointsTransformOverrides();
  // Stores the position of a moved placeholder in the store
  const onPlaceholderMoved = useCallback(
    (element: IElement, newPosition: Vector3) => {
      const locationMarker = newLocationMarkers.find(
        (marker) => marker.id === element.id,
      );

      if (locationMarker) {
        Analytics.track(EventType.moveLocationMarker);
        const movedLocationMarker = {
          ...locationMarker,
          pose: {
            pos: { x: newPosition.x, y: newPosition.y, z: newPosition.z },
            gps: null,
            isWorldRot: false,
            scale: null,
            rot: null,
          },
        };
        const updatedLocationMarkers = newLocationMarkers.filter(
          (marker) => marker.id !== element.id,
        );
        updatedLocationMarkers.push(movedLocationMarker);
        return updateNewLocationMarkers(updatedLocationMarkers);
      }
      const { elementToMove, localPose } = calculateLocalPoseForMovedWaypoint(
        element,
        newPosition,
      );

      return setTransformOverride(elementToMove.id, localPose);
    },
    [newLocationMarkers, setTransformOverride, updateNewLocationMarkers],
  );

  const { isPlaceholderDisabledForMove, isPlaceholderDisabledForClick } =
    useComputeAllowedWaypointInteractions(isInMinimap, isWaypointMoveActive);

  const labelContainer = useViewOverlayRef();

  useImperativeHandle(ref, () => ({
    hideWaypointPreview: () => {
      setHoveredPlaceholder(undefined);
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = undefined;
      }
      clearHoverPreview();
    },
  }));

  const onWaypointHovered = useCallback(
    (target: IElementWaypoint | undefined) => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = undefined;
      }

      if (target === undefined) {
        hoverTimeoutRef.current = setTimeout(() => {
          if (isPreviewHoveredRef.current) return;
          clearHoverPreview();
        }, PREVIEW_CLOSE_DELAY_MS);
        return;
      }

      setHoveredPlaceholder(target);
    },
    [clearHoverPreview],
  );

  const onPreviewHoverChange = useCallback(
    (hovered: boolean) => {
      isPreviewHoveredRef.current = hovered;

      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = undefined;
      }

      if (!hovered) {
        hoverTimeoutRef.current = setTimeout(
          clearHoverPreview,
          PREVIEW_CLOSE_DELAY_MS,
        );
      }
    },
    [clearHoverPreview],
  );

  useEffect(
    () => () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    },
    [],
  );

  const [waypointsRenderer, setWaypointsRenderer] =
    useState<MapWaypointsRendererRef | null>(null);

  useOnWaypointsSelectedInTree(waypointsRenderer, placeholders);

  const { selectedTexture, selectedFlashScanTexture } =
    useWaypointsSelectedTextures();

  return (
    <>
      <SheetsRenderer
        sheets={sheets}
        transparentSheet={transparentSheet}
        pickingToolsCallbacks={pickingToolsCallbacks}
        clickOnSheet={clickOnSheet}
        onSheetContextMenu={onSceneContextMenu}
        enableSheetViews={enableSheetViews}
      />
      <SheetModeControls camera={camera} referencePlaneHeight={position[1]} />
      {!isTransitioning && !pickingToolsCallbacks && (
        <>
          {shouldShowPath && (
            <OdometryPathsRenderer
              paths={paths}
              sheetForElevation={sheetElementForElevation}
              activePathIds={activePathIds}
              onPlaceholderHovered={onWaypointHovered}
              onPlaceholderClicked={onPathWaypointClicked}
              onSceneContextMenu={onSceneContextMenu}
              onPathClick={onPathActivated}
              clippingPlanes={clippingPlanes}
            />
          )}
          {!isInMinimap && !isWaypointMoveActive && hoveredPlaceholder && (
            <WaypointPreview
              placeholder={hoveredPlaceholder}
              onPreviewHoverChange={onPreviewHoverChange}
            />
          )}
          {annotations && !isWaypointMoveActive && (
            <AnnotationsRenderer
              annotations={annotations}
              depthTest={false}
              renderOrder={SheetModeRenderOrders.MeasurementsAndAnnotations}
              fadeOff={false}
              clippingPlanes={clippingPlanes}
            />
          )}
          <SheetWaypoints
            ref={setWaypointsRenderer}
            paths={paths}
            placeholders={placeholders}
            transformOverrides={transformOverrides}
            sheetForElevation={sheetElementForElevation}
            clippingPlanes={clippingPlanes}
            onPlaceholderClicked={onWaypointClicked}
            onPlaceholderContextMenu={onSceneContextMenu}
            onPlaceholderHovered={onWaypointHovered}
            onPlaceholderMoved={onPlaceholderMoved}
            enableMoving={isWaypointMoveActive}
            isPlaceholderDisabledForMove={isPlaceholderDisabledForMove}
            isPlaceholderDisabledForClick={isPlaceholderDisabledForClick}
            isInMinimap={isInMinimap}
            textures={isInMinimap ? {} : { selectedTexture }}
            flashTextures={
              isInMinimap ? {} : { selectedTexture: selectedFlashScanTexture }
            }
          />
          {editingLocationMarker && (
            <LocationMarkerNameEditor
              newLocationMarker={editingLocationMarker}
              editingRightAfterCreation={editingRightAfterCreation}
              parentRef={labelContainer}
              onExit={() => {
                setEditingLocationMarkerId(undefined);
              }}
            />
          )}
        </>
      )}
      {!isTransitioning && measurements && (
        <MeasurementsRenderer
          measurements={measurements}
          isPickingToolActive={!!pickingToolsCallbacks}
          depthTest={false}
          renderOrder={SheetModeRenderOrders.MeasurementsAndAnnotations}
          clippingPlanes={clippingPlanes}
        />
      )}

      {/*
       * The enabled flag from DesaturationPipeline is not used here so that the components using SheetSceneBase
       * need not have an instance of a EffectPipeline when there is no desaturateSheet tool in them
       *
       * Having this EffectPipeline along with another one in a R3f canvas or in a View
       * using SheetSceneBase would conflict with each other
       */}
      <DesaturationSheetsPipeline
        enabled={activeTool === ToolName.desaturateSheet}
        transparentBackground
      />

      <PinMarkerRenderer isInMinimap={isInMinimap} />
      {IS_DEV && !isInMinimap && <ObjectCacheValidator />}
    </>
  );
});

function ObjectCacheValidator(): null {
  useValidateCacheSize(IElementType.img360, 1);
  useValidateCacheSize(IElementType.img2d, 1);
  return null;
}
